import { Button } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    getNewAddedItemsTotal,
    getSelectedCatalogItems,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { addCustomListItems, getCustomListDetail } from 'entities/CustomList';
import { getRouteCatalogList } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { Loader } from 'shared/ui/Loader/Loader';

import { buildAddCustomListItemsPayload } from '../../lib/buildAddCustomListItemsPayload';

interface AddItemsToCustomListButtonProps {
    listId: string;
    propertyId: string;
}

export const AddItemsToCustomListButton = memo(
    ({ listId, propertyId }: AddItemsToCustomListButtonProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const navigate = useNavigate();
        const params = useParams();
        const selectedItems = useSelector(getSelectedCatalogItems);
        const newSelectedCount = useSelector(getNewAddedItemsTotal);
        const listDetail = useSelector(getCustomListDetail);
        const [loading, setLoading] = useState(false);

        const disabled = loading || newSelectedCount === 0;
        const listName = listDetail?.name ?? '';

        const onSubmit = useCallback(async () => {
            const data = buildAddCustomListItemsPayload(selectedItems);
            const addedCount =
                (data.items?.length ?? 0) + (data.globalItems?.length ?? 0);

            if (addedCount === 0) {
                toast.error(t('customLists.failedToAddItems'));
                return;
            }

            setLoading(true);
            const result = await dispatch(addCustomListItems({ listId, data }));
            setLoading(false);

            if (result.meta.requestStatus === 'fulfilled') {
                toast(
                    addedCount === 1
                        ? t('customLists.itemAddedToList', {
                              name: listName,
                          })
                        : t('customLists.itemsAddedToList', {
                              count: addedCount,
                              name: listName,
                          }),
                );
                dispatch(catalogItemActions.removeAllSelection());
                navigate(
                    getRouteCatalogList(
                        params.propertyId || propertyId,
                        listId,
                    ),
                );
            } else {
                const payload = result.payload;
                const message = Array.isArray(payload)
                    ? payload.join(' ')
                    : typeof payload === 'string'
                    ? payload
                    : t('customLists.failedToAddItems');
                toast.error(message);
            }
        }, [
            dispatch,
            listId,
            listName,
            navigate,
            params.propertyId,
            propertyId,
            selectedItems,
            t,
        ]);

        return (
            <Button
                variant="contained"
                disabled={disabled}
                startIcon={loading ? <Loader color="inherit" /> : undefined}
                onClick={onSubmit}
                data-testclass="add-items-to-custom-list-submit"
            >
                {newSelectedCount > 0
                    ? t('customLists.addCountToList', {
                          count: newSelectedCount,
                      })
                    : t('customLists.addToList')}
            </Button>
        );
    },
);
